import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Image } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ImageExportProps {
  text: string;
  styleName?: string;
  asMenuItem?: boolean;
}

const BACKGROUND_PRESETS = [
  { label: "White", value: "#ffffff" },
  { label: "Black", value: "#000000" },
  { label: "Transparent", value: "transparent" },
  { label: "Coral", value: "#E6704D" },
  { label: "Salmon", value: "#FA8072" },
  { label: "Blue", value: "#3498DB" },
  { label: "Green", value: "#2ECC71" },
  { label: "Yellow", value: "#F1C40F" },
  { label: "Pink", value: "#FF69B4" },
  { label: "Gradient Coral", value: "linear-gradient(135deg, #E6704D 0%, #D4553A 100%)" },
  { label: "Gradient Sunset", value: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" },
  { label: "Gradient Ocean", value: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
];

const TEXT_COLORS = [
  { label: "Black", value: "#000000" },
  { label: "White", value: "#ffffff" },
  { label: "Gray", value: "#666666" },
  { label: "Coral", value: "#E6704D" },
  { label: "Blue", value: "#3498db" },
  { label: "Green", value: "#2ecc71" },
  { label: "Red", value: "#e74c3c" },
  { label: "Gold", value: "#f39c12" },
];

const ASPECT_RATIO_PRESETS = [
  { label: "Auto (fit text)", value: "auto", width: 0, height: 0 },
  { label: "Square (1080×1080)", value: "square", width: 1080, height: 1080 },
  { label: "Story (1080×1920)", value: "story", width: 1080, height: 1920 },
  { label: "Landscape (1920×1080)", value: "landscape", width: 1920, height: 1080 },
  { label: "Twitter Post (1200×675)", value: "twitter", width: 1200, height: 675 },
  { label: "Facebook Post (1200×630)", value: "facebook", width: 1200, height: 630 },
  { label: "Custom", value: "custom", width: 0, height: 0 },
];

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number, bgColor: string, opts?: { previewTransparency?: boolean }) {
  if (bgColor === "transparent") {
    if (opts?.previewTransparency) {
      // Checkerboard pattern so transparent looks visible in preview
      const checkSize = 10;
      for (let x = 0; x < width; x += checkSize) {
        for (let y = 0; y < height; y += checkSize) {
          ctx.fillStyle = ((x + y) / checkSize) % 2 === 0 ? "#ccc" : "#fff";
          ctx.fillRect(x, y, checkSize, checkSize);
        }
      }
    } else {
      ctx.clearRect(0, 0, width, height);
    }
    return;
  }

  if (bgColor.startsWith("linear-gradient")) {
    const gradientMatch = bgColor.match(/linear-gradient\((\d+)deg,\s*(#\w+)\s+\d+%,\s*(#\w+)\s+\d+%\)/);
    if (gradientMatch) {
      const angle = parseInt(gradientMatch[1]);
      const color1 = gradientMatch[2];
      const color2 = gradientMatch[3];

      const angleRad = (angle - 90) * Math.PI / 180;
      const x1 = width / 2 - Math.cos(angleRad) * (width / 2);
      const y1 = height / 2 - Math.sin(angleRad) * (height / 2);
      const x2 = width / 2 + Math.cos(angleRad) * (width / 2);
      const y2 = height / 2 + Math.sin(angleRad) * (height / 2);

      const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
      gradient.addColorStop(0, color1);
      gradient.addColorStop(1, color2);
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = "#ffffff";
    }

    ctx.fillRect(0, 0, width, height);
    return;
  }

  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, width, height);
}

function wrapTextToWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const trimmed = text.trim();
  if (!trimmed) return ["Your Text Here"];
  if (maxWidth <= 0) return [trimmed];

  // Prefer word wrapping when spaces exist; otherwise fall back to character wrapping.
  const hasSpaces = /\s/.test(trimmed);
  const tokens = hasSpaces ? trimmed.split(/\s+/) : trimmed.split("");
  const joiner = hasSpaces ? " " : "";

  const lines: string[] = [];
  let current = "";

  const pushCurrent = () => {
    const c = current.trim();
    if (c) lines.push(c);
    current = "";
  };

  for (const token of tokens) {
    const next = current ? `${current}${joiner}${token}` : token;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
      continue;
    }

    // If a single token cannot fit on an empty line, break it into smaller chunks.
    if (!current) {
      if (hasSpaces) {
        // Break long word by characters
        let chunk = "";
        for (const ch of token) {
          const candidate = chunk + ch;
          if (ctx.measureText(candidate).width <= maxWidth) {
            chunk = candidate;
          } else {
            if (chunk) lines.push(chunk);
            chunk = ch;
          }
        }
        if (chunk) lines.push(chunk);
      } else {
        // Character mode: maxWidth exceeded; push token as its own line
        lines.push(token);
      }
    } else {
      pushCurrent();
      current = token;
    }
  }

  pushCurrent();
  return lines.length ? lines : [trimmed];
}

export function ImageExport({ text, styleName, asMenuItem = false }: ImageExportProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(32);
  const [padding, setPadding] = useState(40);
  const [aspectRatio, setAspectRatio] = useState("auto");
  const [customWidth, setCustomWidth] = useState(720);
  const [customHeight, setCustomHeight] = useState(720);
  const [exportScale, setExportScale] = useState(2);
  const [background, setBackground] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#000000");
  const [customBackground, setCustomBackground] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { toast } = useToast();

  // Derive dimensions from aspect ratio selection
  const { canvasWidth: fixedWidth, canvasHeight: fixedHeight, isFixed } = useMemo(() => {
    const preset = ASPECT_RATIO_PRESETS.find((p) => p.value === aspectRatio);
    if (!preset || preset.value === "auto") {
      return { canvasWidth: 0, canvasHeight: 0, isFixed: false };
    }
    if (preset.value === "custom") {
      return { canvasWidth: customWidth, canvasHeight: customHeight, isFixed: true };
    }
    return { canvasWidth: preset.width, canvasHeight: preset.height, isFixed: true };
  }, [aspectRatio, customWidth, customHeight]);

  const displayText = useMemo(() => text.trim() || "Your Text Here", [text]);

  const fontFamily = useMemo(
    () => '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif',
    []
  );

  const getLayout = (ctx: CanvasRenderingContext2D) => {
    ctx.font = `${fontSize}px ${fontFamily}`;
    const lineHeight = fontSize * 1.2;

    if (isFixed && fixedWidth > 0 && fixedHeight > 0) {
      // Fixed aspect ratio: wrap text to fit within padding, use fixed dimensions
      const contentWidth = Math.max(1, fixedWidth - padding * 2);
      const lines = wrapTextToWidth(ctx, displayText, contentWidth);
      return { lines, lineHeight, canvasWidth: fixedWidth, canvasHeight: fixedHeight };
    }

    // Auto mode: canvas height adapts to text
    const contentWidth = Math.max(1, customWidth - padding * 2);
    const lines = wrapTextToWidth(ctx, displayText, contentWidth);
    const textBlockHeight = lines.length * lineHeight;
    const canvasHeight = Math.ceil(textBlockHeight + padding * 2);

    return { lines, lineHeight, canvasWidth: Math.ceil(customWidth), canvasHeight };
  };

  const renderToCanvas = (
    opts: {
      scale: number;
      previewTransparency?: boolean;
    }
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bgColor = customBackground || background;

    // Compute layout in CSS pixels
    const { lines, lineHeight, canvasWidth, canvasHeight } = getLayout(ctx);

    // Set backing store in device pixels
    canvas.width = Math.max(1, Math.round(canvasWidth * opts.scale));
    canvas.height = Math.max(1, Math.round(canvasHeight * opts.scale));

    // Keep display size stable in CSS pixels
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    // Reset and scale drawing
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(opts.scale, opts.scale);

    drawBackground(ctx, canvasWidth, canvasHeight, bgColor, {
      previewTransparency: opts.previewTransparency,
    });

    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = textColor;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    const centerX = canvasWidth / 2;

    // For fixed aspect ratios, center text block vertically
    const textBlockHeight = lines.length * lineHeight;
    const startY = isFixed
      ? (canvasHeight - textBlockHeight) / 2 + lineHeight / 2
      : padding + lineHeight / 2;

    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], centerX, startY + i * lineHeight);
    }
  };

  const generateImage = async () => {
    setIsGenerating(true);

    try {
      // Use user-selected export scale for output size/quality.
      renderToCanvas({ scale: exportScale, previewTransparency: false });

      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const fileName = styleName
          ? `text-onl-${styleName.toLowerCase().replace(/\s+/g, "-")}.png`
          : "text-onl-export.png";
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast({
          title: "Image downloaded!",
          description: `Saved as ${fileName}`,
        });
      }, "image/png");
    } catch {
      toast({
        title: "Export failed",
        description: "Could not generate image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);

      // Re-render preview after export so the preview stays crisp and uses preview scale.
      const previewScale = Math.max(1, window.devicePixelRatio || 1);
      renderToCanvas({ scale: previewScale, previewTransparency: true });
    }
  };

  // Ensure preview renders reliably on mobile when the dialog opens.
  useEffect(() => {
    if (!isOpen) return;

    const previewScale = Math.max(1, window.devicePixelRatio || 1);

    // 2 RAFs gives the dialog time to mount + layout the canvas before painting.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        renderToCanvas({ scale: previewScale, previewTransparency: true });
      });
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update preview when settings change
  useEffect(() => {
    if (!isOpen) return;
    const previewScale = Math.max(1, window.devicePixelRatio || 1);
    renderToCanvas({ scale: previewScale, previewTransparency: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fontSize, padding, background, textColor, customBackground, displayText, aspectRatio, customWidth, customHeight, isFixed, fixedWidth, fixedHeight]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {asMenuItem ? (
        <div
          className="flex items-center cursor-pointer w-full"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (text.trim()) setIsOpen(true);
          }}
        >
          <Image size={14} className="mr-2" />
          Export Image
        </div>
      ) : (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" disabled={!text.trim()}>
            <Image size={16} />
            Export Image
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-md md:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Image size={18} />
            Export as Image
          </DialogTitle>
          <DialogDescription className="text-xs">
            Customize and download your styled text as a PNG.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-1 pr-1">
          {/* Preview */}
          <div className="flex justify-center items-center p-2 bg-muted/30 rounded-lg overflow-hidden min-h-[80px] max-h-[100px]">
            <canvas
              ref={canvasRef}
              className="border border-border rounded object-contain"
              style={{ maxWidth: "100%", maxHeight: "80px" }}
            />
          </div>

          {/* Two columns for Font Size and Padding on tablet+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Font Size */}
            <div className="space-y-1">
              <div className="flex justify-between">
                <Label className="text-xs">Font Size</Label>
                <span className="text-xs text-muted-foreground">{fontSize}px</span>
              </div>
              <Slider
                value={[fontSize]}
                onValueChange={(value) => setFontSize(value[0])}
                min={16}
                max={96}
                step={2}
              />
            </div>

            {/* Padding */}
            <div className="space-y-1">
              <div className="flex justify-between">
                <Label className="text-xs">Padding</Label>
                <span className="text-xs text-muted-foreground">{padding}px</span>
              </div>
              <Slider
                value={[padding]}
                onValueChange={(value) => setPadding(value[0])}
                min={10}
                max={100}
                step={5}
              />
            </div>
          </div>

          {/* Two columns for Aspect Ratio and Export Size on tablet+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Aspect Ratio */}
            <div className="space-y-1">
              <Label className="text-xs">Aspect Ratio</Label>
              <Select value={aspectRatio} onValueChange={setAspectRatio}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Choose aspect ratio" />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIO_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Export Size / Quality */}
            <div className="space-y-1">
              <Label className="text-xs">Export Size</Label>
              <Select value={String(exportScale)} onValueChange={(v) => setExportScale(Number(v))}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1× (Standard)</SelectItem>
                  <SelectItem value="2">2× (High)</SelectItem>
                  <SelectItem value="3">3× (Very High)</SelectItem>
                  <SelectItem value="4">4× (Ultra)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom Width/Height (only for Auto or Custom) */}
          {(aspectRatio === "auto" || aspectRatio === "custom") && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <div className="flex justify-between">
                  <Label className="text-xs">Width</Label>
                  <span className="text-xs text-muted-foreground">{customWidth}px</span>
                </div>
                <Slider
                  value={[customWidth]}
                  onValueChange={(value) => setCustomWidth(value[0])}
                  min={240}
                  max={1920}
                  step={20}
                />
              </div>
              {aspectRatio === "custom" && (
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <Label className="text-xs">Height</Label>
                    <span className="text-xs text-muted-foreground">{customHeight}px</span>
                  </div>
                  <Slider
                    value={[customHeight]}
                    onValueChange={(value) => setCustomHeight(value[0])}
                    min={240}
                    max={1920}
                    step={20}
                  />
                </div>
              )}
            </div>
          )}

          {/* Two columns for Text Color and Background on tablet+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Text Color */}
            <div className="space-y-1">
              <Label className="text-xs">Text Color</Label>
              <Select value={textColor} onValueChange={setTextColor}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Color" />
                </SelectTrigger>
                <SelectContent>
                  {TEXT_COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded border" style={{ background: color.value }} />
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Background */}
            <div className="space-y-1">
              <Label className="text-xs">Background</Label>
              <Select value={background} onValueChange={setBackground}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select background" />
                </SelectTrigger>
                <SelectContent>
                  {BACKGROUND_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded border"
                          style={{
                            background:
                              preset.value === "transparent"
                                ? "repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 6px 6px"
                                : preset.value,
                          }}
                        />
                        {preset.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Custom background input */}
          <div className="space-y-1">
            <Label className="text-xs">Custom Background</Label>
            <Input
              placeholder="e.g., #FF5733 or rgb(255,87,51)"
              value={customBackground}
              onChange={(e) => setCustomBackground(e.target.value)}
              className="h-8 text-xs"
            />
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 pt-3 border-t border-border mt-2">
          <Button variant="outline" size="sm" onClick={() => setIsOpen(false)} className="text-xs">
            Cancel
          </Button>
          <Button size="sm" onClick={generateImage} disabled={isGenerating} className="gap-1.5 text-xs">
            <Download size={14} />
            {isGenerating ? "Generating..." : "Download PNG"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
